import * as React from 'react';
import { Select, type SelectProps } from 'antd';
import { useClickOutsideWithPopup } from '../../utils';
import * as Store from '../store';
import DimensionSelector from './DimensionSelector';
import CustomTagSelector from './CustomTagSelector';
import * as Styled from './styled';

export interface FieldSelectorProps {
  defaultType: 'dimension' | 'tag';
  defaultValue: { label: string; value: string };
  status?: SelectProps['status'];
  onChange?: (value: string, type: FieldSelectorProps['defaultType']) => void;
  onCancel?: () => void;
  style?: React.CSSProperties;
}

const DEFAULT_ACTION_OPTIONS = [
  {
    label: 'in Dimensions',
    value: 'dimension' as const,
  },
  {
    label: 'in Custom Tags',
    value: 'tag' as const,
  },
];

const selectorStyle = { maxWidth: 'calc(100% - 140px)', flex: 1 };

const FieldSelector = (props: FieldSelectorProps) => {
  const [type, setType] = React.useState(props.defaultType || 'dimension');
  const meta = Store.useFilterMeta();
  const propsRef = React.useRef(props);
  propsRef.current = props;

  const [containerRef, outsidePopupClassName] = useClickOutsideWithPopup(
    React.useCallback(() => propsRef.current.onCancel?.(), []),
  );

  return (
    <div ref={containerRef} style={{ borderRadius: 6, maxWidth: '100%', ...props.style }}>
      <Styled.AtomSpaceCompact>
        {type === 'dimension' && (
          <DimensionSelector
            disableFilterFirstRender
            defaultValue={props.defaultType === 'dimension' ? props.defaultValue.label : undefined}
            status={props.status}
            classNames={{ popup: { root: outsidePopupClassName } }}
            style={selectorStyle}
            onSelect={(value) => props.onChange?.(value, 'dimension')}
          />
        )}
        {type === 'tag' && (
          <CustomTagSelector
            defaultValue={props.defaultType === 'tag' ? props.defaultValue.label : undefined}
            status={props.status}
            classNames={{ popup: { root: outsidePopupClassName } }}
            style={selectorStyle}
            onSelect={(value) => props.onChange?.(value, 'tag')}
          />
        )}
        <Select
          value={type}
          options={DEFAULT_ACTION_OPTIONS.filter(
            (it) => it.value !== 'tag' || meta.dimensions.find((it) => it.name === 'Tag'),
          )}
          virtual={false}
          status={props.status}
          classNames={{ popup: { root: outsidePopupClassName } }}
          style={{ width: 140 }}
          onChange={setType}
        />
      </Styled.AtomSpaceCompact>
    </div>
  );
};

export default React.memo(FieldSelector);
